import React, { useEffect, lazy, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CONFIG } from './config/variables';
import { initializePreviewProtection } from './utils/protection';
import { useCMSData } from './hooks/useCMSData';

// Components
import PreviewWatermark from './components/PreviewWatermark';
import PreviewNotice from './components/PreviewNotice';
import Header from './components/Header';
import Hero from './components/Hero';
import TrustStrip from './components/TrustStrip';
import SystemAuthority from './components/SystemAuthority';
import Problem from './components/Problem';
import Solution from './components/Solution';
import USP from './components/USP';
import BeforeAfter from './components/BeforeAfter';
import WhatYouGet from './components/WhatYouGet';
import OfferStack from './components/OfferStack';
import HowItWorks from './components/HowItWorks';
import WhoThisIsFor from './components/WhoThisIsFor';
import Reviews from './components/Reviews';
import SamplePages from './components/SamplePages';
import ResponsivePrecision from './components/ResponsivePrecision';
import LocalCoverage from './components/LocalCoverage';
import Guarantee from './components/Guarantee';
import Investment from './components/Investment';
import FAQ from './components/FAQ';
import InvestmentCalculator from './components/InvestmentCalculator';
import PaymentFlow from './components/PaymentFlow';
import Payment from './components/Payment';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import SyncTick from './components/SyncTick';

// Lazy-loaded pages (non-critical routes)
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));

import './App.css';

const LandingPage = () => (
  <>
    <Hero />
    <TrustStrip />
    <SystemAuthority />
    <Problem />
    <Solution />
    <USP />
    <BeforeAfter />
    <WhatYouGet />
    <OfferStack />
    <HowItWorks />
    <WhoThisIsFor />
    <Reviews />
    <SamplePages />
    <ResponsivePrecision />
    <LocalCoverage />
    <Guarantee />
    <Investment />
    <FAQ />
    <InvestmentCalculator />
    <PaymentFlow />
    <Payment />
    <FinalCTA />
  </>
);

// Loading fallback for lazy routes
const RouteLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-white/60 font-medium">Loading...</p>
    </div>
  </div>
);

function App() {
  const { synced } = useCMSData();
  const [showSyncTick, setShowSyncTick] = useState(false);

  useEffect(() => {
    initializePreviewProtection();
  }, []);

  // Show sync tick after successful CMS sync
  useEffect(() => {
    if (synced && CONFIG.BPAS_CMS_URL && CONFIG.BPAS_CMS_URL !== 'TOBEUPDATED') {
      setShowSyncTick(true);
      const timer = setTimeout(() => setShowSyncTick(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [synced]);

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <title>{CONFIG.PRIMARY_SERVICE} in {CONFIG.PRIMARY_LOCATION} | {CONFIG.BUSINESS_NAME}</title>
        {CONFIG.PREVIEW_MODE && <PreviewWatermark />}
        {CONFIG.PREVIEW_MODE && <PreviewNotice />}
        {showSyncTick && <SyncTick />}
        <Header />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/preview/:previewId" element={<Suspense fallback={<RouteLoader />}><PreviewPage /></Suspense>} />
          <Route path="/onboarding" element={<Suspense fallback={<RouteLoader />}><OnboardingPage /></Suspense>} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;